import React, { useEffect, useState, useRef } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { setUserScroll } from '../Block/Block';
import useXapi from '../../Hooks/useXapi';
import { navigate } from '../../redux/actions/CourseActions';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import CmsTools from '../CmsTools/CmsTools';
import './SideNav.scss';
import Loader from '../Block/common/Loading/Loading';
import { pseudolocalize } from '../Block/common/pseudolocalize/pseudolocalize';

// Updates the active class name. Faster than doing it through React re-renders.
// It's a beefy unordered list.
// Method gets exported and used here, and in block.js
export const updateActiveSideNav = chunk => {
  let el = document.querySelector(`#side-nav-${chunk.li.split('.').join('-')}`);
  let prevActiveList = document.querySelectorAll('.chunklink-active');
  prevActiveList.forEach(prevActive => {
    prevActive.classList.remove('chunklink-active');
  });
  el.className += ' chunklink-active';
};

const SideNav = () => {
  const { xApiEvent } = useXapi();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    isNavVisible,
    learningPathJson,
    currentBlock,
    isPseudo,
    currentModule
  } = useSelector(
    state => ({
      learningPathJson: state.CourseState.learningPathJson,
      currentBlock: state.CourseState.currentBlock,
      currentModule: state.CourseState.currentModule,
      isPseudo: state.LanguageState.isPseudo,
      isNavVisible: state.SideNavState.isNavVisible
    }),
    shallowEqual
  );

  // active chunk li.
  // no longer through redux as this seems more performant.
  let locString = window.location.pathname.endsWith('/')
    ? window.location.pathname.slice(0, -1)
    : window.location.pathname;
  let activeChunk = locString.split('/').pop();

  let navRef = useRef(null);
  let currentBlockRef = useRef(null);
  let blankRef = useRef(null);
  let splitLI, currentBlockLi;

  splitLI = activeChunk.split('.');
  currentBlockLi = splitLI
    .filter((li, i) => i !== splitLI.length - 1)
    .join('.');

  // defaults open drawers to active block and mod
  let [openDrawers, setOpenDrawers] = useState([]);
  let [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  // keeps previous drawers open and opens new ones.
  const openMultipleDrawers = items => {
    items.forEach(item => {
      let drawerIndex = openDrawers.findIndex(m => m.li === item.li);
      if (drawerIndex !== -1) {
        openDrawers[drawerIndex].open = true;
      } else {
        openDrawers.push({
          li: item.li,
          open: true
        });
      }
    });
    setOpenDrawers(JSON.parse(JSON.stringify(openDrawers)));
  };

  const toggleDrawers = item => {
    let drawerIndex = openDrawers.findIndex(m => m.li === item.li);
    if (drawerIndex !== -1) {
      openDrawers[drawerIndex].open = !openDrawers[drawerIndex].open;
    } else {
      openDrawers.push({
        li: item.li,
        open: true
      });
    }
    setOpenDrawers(JSON.parse(JSON.stringify(openDrawers)));
  };

  const drawerOpen = item => {
    let itemState = openDrawers.find(m => m.li === item.li);
    let isOpen = itemState && itemState.open ? true : false;
    return isOpen;
  };

  const skipNavigation = e => {
    e.preventDefault();
    const chunksContainer = document.getElementById('chunks-container');
    chunksContainer.setAttribute('tabindex', '0');
    setTimeout(() => {
      chunksContainer.focus();
      chunksContainer.removeAttribute('tabindex');
    });
  };

  const scrollToView = () => {
    const sideNav = document.querySelector('#side-nav');
    const activeItem = document.querySelector(
      `#side-nav-${currentBlockLi.split('.').join('-')} > a`
    );
    const activeItemDims = activeItem.getBoundingClientRect();

    // position requires multiple offsets based on mobileNavOpen and viewportWidth
    const viewportWidth = window.innerWidth;
    let position =
      viewportWidth > 767
        ? // we're in desktop view
          activeItemDims.y - activeItemDims.height + sideNav.scrollTop - 83
        : // we're in mobile view
        isMobileNavOpen
        ? // mobileNav is visible
          activeItemDims.y - activeItemDims.height + sideNav.scrollTop - 373
        : // mobileNav is hidden
          activeItemDims.y - activeItemDims.height + sideNav.scrollTop - 109;
    setTimeout(() => {
      sideNav.scroll({
        top: position,
        behavior: 'smooth'
      });
    });
  };

  // runs when currentBlock changes
  useEffect(() => {
    // if we're not on the homepage
    if (learningPathJson['@id'] !== activeChunk) {
      // open new drawers
      openMultipleDrawers([currentModule, currentBlock]);
      // set the active chunk
      // eslint-disable-next-line react-hooks/exhaustive-deps
      activeChunk = window.location.pathname.split('/').pop();
      // updated highlight on active chunk
      updateActiveSideNav({ li: activeChunk });
      // scroll to the chunk.
      // noticable delay between click and active highlight without setTimeout.
      setTimeout(() => {
        scrollToView();
      });
    }
  }, [currentBlock]);

  // Triggers the resize window event so that svg media items
  // can resize in fixed window scenarios (tablets, mobile)
  useEffect(() => {
    // give the side nav a chance to run its animation
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 325);
  }, [isNavVisible]);

  if (!activeChunk) return <Loader context="SideNav" />;

  return (
    <nav
      id="side-nav"
      className={`sidebar sidebar--indigo ${isNavVisible && 'isVisible'}`}
    >
      <a
        onClick={e => skipNavigation(e)}
        className="skip-link"
        href="#chunks-container"
      >
        {t('SideNav.skipToContent')}
      </a>
      <div className="sidebar__header">
        <div className="sidebar__header-title">
          {isPseudo
            ? pseudolocalize(learningPathJson.name)
            : learningPathJson.name}
        </div>
      </div>
      <div className={`mobile-nav ${isMobileNavOpen ? 'isVisible' : ''}`}>
        <ul className="sidebar__nav">
          <li className="nav-item">
            <button
              aria-label={t('RecentlyViewed.recentlyViewed')}
              onClick={() => dispatch({ type: 'SHOW_RV' })}
            >
              <span className="icon-recent-apps icon"></span>
              {t('RecentlyViewed.recentlyViewed')}
            </button>
          </li>
          <li className="nav-item">
            <button
              aria-label={t('Bookmarks.bookmarks')}
              onClick={() => dispatch({ type: 'SHOW_BOOKMARKS' })}
            >
              <span className="icon-bookmark icon"></span>
              {t('Bookmarks.bookmarks')}
            </button>
          </li>
          <li className="nav-item">
            <button
              aria-label={t('CourseIndex.courseIndex')}
              onClick={() => dispatch({ type: 'SHOW_INDEX' })}
            >
              <span className="icon-list-view icon"></span>
              {t('CourseIndex.courseIndex')}
            </button>
          </li>
          <li className="nav-item">
            <button
              aria-label={t('Search.search')}
              onClick={() => dispatch({ type: 'SHOW_SEARCH' })}
            >
              <span className="icon-search icon"></span>
              {t('Search.search')}
            </button>
          </li>
          <li className="nav-item">
            <button
              aria-label={t('Language.language')}
              onClick={() => dispatch({ type: 'SHOW_LANGUAGES' })}
            >
              <span className="icon-language icon"></span>
              {t('Language.language')}
            </button>
          </li>
        </ul>
      </div>
      <div
        className={`toggle-mobileNav ${
          isMobileNavOpen ? 'mobile-nav-open' : ''
        }`}
        onClick={() => setIsMobileNavOpen(!isMobileNavOpen)}
      >
        {isMobileNavOpen ? 'Hide' : 'Show'} Menu
      </div>
      {learningPathJson.hasPart.length && (
        <ul ref={navRef} className="main-sidebar__nav">
          {learningPathJson.hasPart.map((mod, moduleIndex) => {
            return (
              <li
                key={moduleIndex}
                className={`sidebar__drawer ${
                  drawerOpen(mod) ? 'sidebar__drawer--opened' : ''
                }`}
              >
                <a
                  href={` #${mod.li}`}
                  className={`navlink modulelink${
                    isMobileNavOpen ? ' mobileNavOpen' : ''
                  }`}
                  onClick={e => {
                    e.preventDefault();
                    toggleDrawers(mod);
                  }}
                >
                  {/* Module label */}
                  <span className="navlink_labelcontainer-wrapper">
                    <CmsTools id={mod.id} type="modules" />
                    <span className="navlink_labelcontainer">
                      <span className="navlink_label">{mod.li}</span>
                    </span>
                  </span>
                  {/* Module name */}
                  <span className="navlink_namecontainer-wrapper">
                    <span className="navlink_namecontainer">
                      <span className="navlink_name">
                        {isPseudo ? pseudolocalize(mod.name) : mod.name}
                        <CmsTools
                          id={mod.title_assetid}
                          type="assets"
                          subtype="text"
                        />
                      </span>
                    </span>
                  </span>
                  {/* Chevron Space Container - to take up space for ::after pseudo element*/}
                  <span className="navlink_chevronbutton-wrapper">
                    <button
                      tabIndex="0"
                      className="navlink_chevronbutton"
                      aria-label={t('SideNav.toggleModule')}
                    >
                      {drawerOpen(mod) ? (
                        <span className="icon-chevron-up" />
                      ) : (
                        <span className="icon-chevron-down" />
                      )}
                    </button>
                  </span>
                </a>
                {mod.hasPart.length && (
                  <ul className="sidebar__items">
                    {mod.hasPart.map((block, blockIndex) => (
                      <li
                        ref={
                          block.li === currentBlockLi
                            ? currentBlockRef
                            : blankRef
                        }
                        key={blockIndex}
                        id={`side-nav-${block.li.split('.').join('-')}`}
                        className={`sidebar__drawer ${
                          drawerOpen(block) ? 'sidebar__drawer--opened' : ''
                        }`}
                      >
                        <a
                          className="navlink blocklink"
                          href={`#${block.li}`}
                          onClick={e => {
                            e.preventDefault();
                            toggleDrawers(block);
                          }}
                        >
                          {/* Block label */}
                          <span className="navlink_labelcontainer-wrapper">
                            <span className="navlink_labelcontainer">
                              {/* Block name */}
                              <span className="navlink_label">{block.li}</span>
                            </span>
                          </span>

                          {/* Block name */}
                          <span className="navlink_namecontainer-wrapper">
                            <span className="navlink_namecontainer">
                              <span className="navlink_name">
                                {isPseudo
                                  ? pseudolocalize(block.name)
                                  : block.name}
                              </span>
                            </span>
                            {/* <DevelopmentState
                                label={`sidenav`}
                                state={block.state}
                              /> */}
                          </span>

                          {/* Chevron Space Container - to take up space for ::after pseudo element*/}
                          <span className="navlink_chevronbutton-wrapper">
                            <button
                              className="navlink_chevronbutton"
                              aria-label={t('SideNav.toggleBlock')}
                              tabIndex="0"
                            >
                              {currentBlockLi === block.li ? (
                                <span className="icon-chevron-up" />
                              ) : (
                                <span className="icon-chevron-down" />
                              )}
                            </button>
                          </span>
                        </a>
                        {block.chunks && block.chunks.length && (
                          <ul className="sidebar__items chunks-wrapper">
                            {block.chunks.map((chunk, chunkIndex) => (
                              <li
                                key={chunkIndex}
                                className={`block-chunk sidebar__item`}
                              >
                                <a
                                  tabIndex="0"
                                  id={`side-nav-${chunk.li
                                    .split('.')
                                    .join('-')}`}
                                  className={`navlink chunklink ${
                                    chunk.li === activeChunk
                                      ? 'chunklink-active'
                                      : ''
                                  }`}
                                  href={
                                    learningPathJson['@id'] !== activeChunk
                                      ? window.location.href.replace(
                                          activeChunk,
                                          chunk.li
                                        )
                                      : `${window.location.host}/${activeChunk}/${chunk.li}`
                                  }
                                  onClick={e => {
                                    e.preventDefault();
                                    setUserScroll(false);
                                    updateActiveSideNav(chunk);
                                    toast.dismiss();
                                    setTimeout(() => {
                                      dispatch(navigate(chunk));
                                      xApiEvent(
                                        'Side Navigation clicked',
                                        window.location.href
                                      );
                                    });
                                  }}
                                >
                                  {/* Chunk label */}
                                  <span className="navlink_labelcontainer-wrapper">
                                    <span className="navlink_labelcontainer">
                                      {/* Chunk label */}
                                      <span className="navlink_label">
                                        {chunk.li}
                                      </span>
                                    </span>
                                  </span>
                                  {/* Chunk name */}
                                  <span className="navlink_namecontainer-wrapper">
                                    <span className="navlink_namecontainer">
                                      <span className="navlink_name">
                                        {isPseudo
                                          ? pseudolocalize(chunk.name)
                                          : chunk.name}
                                      </span>
                                    </span>
                                  </span>
                                </a>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </nav>
  );
};

export default React.memo(SideNav);
